import { useTranslation } from 'react-i18next'
import { localizedName } from '../utils/localizedName'
import { useState, useRef, useEffect } from 'react'
import {
  Box, Text, Button, Stack, Group, Badge, TextInput,
  Center, Loader, Alert, ActionIcon, Modal, Select,
  Textarea, Paper, ThemeIcon, SimpleGrid, Drawer, ScrollArea,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import {
  IconScan, IconSearch, IconCheck, IconAlertCircle,
  IconX, IconTool, IconAlertTriangle, IconClipboardCheck,
  IconPlus, IconArrowLeft, IconDeviceDesktop, IconHistory, IconClock, IconShare,
} from '@tabler/icons-react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { NotFoundException, DecodeHintType, BarcodeFormat } from '@zxing/library'
import { useNavigate } from 'react-router-dom'
import { scanLookupAsset, updateAsset, createRepairJob, reportAssetDamaged, reportAssetLost, markAssetInstalled, uninstallAsset, getVendors, getAssetHistory, getLocations } from '../api/client'
import RepairJobModal from '../components/repair/RepairJobModal'
import SwapAssetModal from '../components/assets/SwapAssetModal'
import SwapWithSpareModal from '../components/repair/SwapWithSpareModal'

const STATUS_COLOR: Record<string, string> = {
  AVAILABLE: 'teal', ASSIGNED: 'blue', BORROWED: 'yellow', INSTALLED: 'cyan',
  REPAIR: 'orange', DAMAGED: 'red', LOST: 'red', WRITE_OFF: 'gray',
}

const QUICK_ACTIONS = [
  { value: 'REPAIR',    labelKey: 'scan.actionRepair',    color: 'orange' },
  { value: 'AVAILABLE', labelKey: 'scan.actionAvailable', color: 'teal' },
  { value: 'LOST',      labelKey: 'scan.actionLost',      color: 'red' },
  { value: 'INSTALLED', labelKey: 'scan.actionInstalled', color: 'cyan' },
  { value: 'UNINSTALL', labelKey: 'scan.actionUninstall', color: 'gray' },
]

export default function ScanPage() {
  const { t, i18n } = useTranslation()
  const STATUS_LABEL: Record<string, string> = {
    AVAILABLE: t('assetStatus.available', 'Available'), ASSIGNED: t('assetStatus.assigned', 'Assigned'),
    BORROWED: t('assetStatus.borrowed', 'Borrowed'), INSTALLED: t('assetStatus.installed', 'Installed'),
    REPAIR: t('assetStatus.repair', 'In Repair'), DAMAGED: t('assetStatus.damaged', 'Damaged'),
    LOST: t('assetStatus.lost', 'Lost'), WRITE_OFF: t('assetStatus.writeOff', 'Written Off'),
  }
  const navigate = useNavigate()
  const { isSmall } = useBreakpoint()
  const scannerRef = useRef<any>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const decodedRef = useRef(false)
  const [scanning, setScanning] = useState(false)
  const [loading, setLoading] = useState(false)
  const [manualInput, setManualInput] = useState('')
  const [asset, setAsset] = useState<any>(null)
  const [notFound, setNotFound] = useState(false)
  const [notFoundCode, setNotFoundCode] = useState('')
  const [actionOpened, { open: openAction, close: closeAction }] = useDisclosure(false)
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const [actionNote, setActionNote] = useState('')
  const [installLocation, setInstallLocation] = useState('')
  const [locations, setLocations] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [repairModalOpen, setRepairModalOpen] = useState(false)
  const [repairVendors, setRepairVendors] = useState<any[]>([])
  const [swapModalOpen, setSwapModalOpen] = useState(false)
  const [swapJobId, setSwapJobId] = useState<number | null>(null)
  const [generalSwapModalOpen, setGeneralSwapModalOpen] = useState(false)
  // ── ซ่อม + มีเครื่องมาแทนไหม (flow ใหม่) ──
  const [askReplaceOpen, { open: openAskReplace, close: closeAskReplace }] = useDisclosure(false)
  const [pendingReplaceSwap, setPendingReplaceSwap] = useState(false) // true = กำลังอยู่ใน flow "สลับก่อนแล้วค่อยเปิด repair modal ต่อ"
  const containerId = 'scan-qr-container'

  // ── Scan Log: ประวัติการสแกนใน session นี้ (เก็บใน sessionStorage กันหายตอน navigate กลับมา) ──
  const [scanLog, setScanLog] = useState<any[]>(() => {
    try {
      const raw = sessionStorage.getItem('quickScanLog')
      return raw ? JSON.parse(raw) : []
    } catch { return [] }
  })
  const [scanLogOpened, { open: openScanLog, close: closeScanLog }] = useDisclosure(false)

  // ── Movement History ของ asset ที่กำลังแสดงอยู่ ──
  const [moveHistory, setMoveHistory] = useState<any[]>([])
  const [loadingMoveHistory, setLoadingMoveHistory] = useState(false)
  const [moveHistoryOpened, { open: openMoveHistory, close: closeMoveHistory }] = useDisclosure(false)

  const pushScanLog = (a: any) => {
    setScanLog(prev => {
      const next = [
        { id: a.id, assetCode: a.assetCode, name: a.name, status: a.status, scannedAt: new Date().toISOString() },
        ...prev.filter(x => x.id !== a.id),
      ].slice(0, 30) // เก็บล่าสุด 30 รายการพอ ไม่ต้องเก็บไม่จำกัด
      try { sessionStorage.setItem('quickScanLog', JSON.stringify(next)) } catch {}
      return next
    })
  }

  const loadMoveHistory = async (assetId: number) => {
    setLoadingMoveHistory(true)
    try {
      const res = await getAssetHistory(assetId)
      setMoveHistory((res.data ?? []).slice(0, 15))
    } catch { setMoveHistory([]) }
    finally { setLoadingMoveHistory(false) }
  }

  const controlsRef = useRef<any>(null)

  // Start camera scanner using ZXing — ปรับให้อ่านบาร์โค้ดยาก (สะท้อนแสง/โค้ง) ได้ดีขึ้น
  const startScanner = async () => {
    try {
      // กันเรียกซ้อนกัน — ถ้ามี scan session เก่าค้างอยู่ ให้ปิดให้สนิทก่อนเปิดใหม่เสมอ
      // (นี่คือสาเหตุหลักที่สแกนหลายรอบแล้วเริ่มพัง: scan loop เก่าไม่เคยถูก stop() จริง)
      if (controlsRef.current) {
        try { controlsRef.current.stop() } catch {}
        controlsRef.current = null
      }

      decodedRef.current = false
      setScanning(true)

      // จำกัด format ที่ใช้จริงกับ asset code/serial เพื่อลด CPU และเพิ่มความเร็ว/แม่นยำ
      // แทนที่จะสแกนทุก format พร้อมกัน (ซึ่งช้ากว่าและ false-negative ง่ายกว่า)
      const hints = new Map()
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.QR_CODE,
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
      ])
      hints.set(DecodeHintType.TRY_HARDER, true)

      const codeReader = new BrowserMultiFormatReader(hints)
      scannerRef.current = codeReader
      const videoElement = videoRef.current
      if (!videoElement) { setScanning(false); return }

      // ขอความละเอียดสูงสุดที่กล้องรองรับ + เปิด continuous autofocus
      // (มือถือบางรุ่นไม่ focus ต่อเนื่องถ้าไม่ขอ advanced constraint ตรงนี้)
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          advanced: [{ focusMode: 'continuous' } as any],
        },
      }

      // เก็บ controls ที่ decodeFromConstraints คืนมา — ต้องเรียก .stop() ของตัวนี้เองตอนหยุดสแกน
      // ไม่ใช่แค่ปล่อย MediaStream ผ่าน static releaseAllStreams() เฉยๆ (ปล่อย stream แต่ไม่ได้
      // หยุด scan loop ภายใน ทำให้ instance ค้างสะสมเมื่อสแกนซ้ำหลายรอบ)
      controlsRef.current = await codeReader.decodeFromConstraints(
        constraints,
        videoElement,
        (result, err) => {
          if (result && !decodedRef.current) {
            decodedRef.current = true
            stopScanner()
            handleLookup(result.getText().trim())
          }
          if (err && !(err instanceof NotFoundException)) {
            // ignore continuous no-result errors
          }
        }
      )
    } catch {
      notifications.show({ color: 'red', message: t('scan.cameraError') })
      setScanning(false)
    }
  }

  const stopScanner = () => {
    try {
      if (controlsRef.current) {
        controlsRef.current.stop()
        controlsRef.current = null
      }
      if (scannerRef.current) {
        BrowserMultiFormatReader.releaseAllStreams()
        scannerRef.current = null
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    } catch {}
    setScanning(false)
  }

  useEffect(() => {
    startScanner()
    return () => { stopScanner() }
  }, [])

  const handleLookup = async (q: string) => {
    if (!q.trim()) return
    setLoading(true)
    setAsset(null)
    setNotFound(false)
    try {
      const res = await scanLookupAsset(q.trim())
      if (res.data.found) {
        setAsset(res.data)
        pushScanLog(res.data)
      } else {
        setNotFoundCode(q.trim())
        setNotFound(true)
      }
    } catch {
      notifications.show({ color: 'red', message: t('scan.genericError') })
    } finally {
      setLoading(false)
    }
  }

  const handleQuickAction = async () => {
    if (!selectedAction || !asset) return
    if (selectedAction === 'INSTALLED' && !installLocation.trim()) {
      notifications.show({ color: 'red', message: t('scan.installLocationRequired', 'Location is required') })
      return
    }
    setSaving(true)
    try {
      if (selectedAction === 'DAMAGED') {
        await reportAssetDamaged(asset.id, { reason: actionNote || 'Reported via Quick Scan', note: actionNote || undefined })
      } else if (selectedAction === 'LOST') {
        await reportAssetLost(asset.id, { note: actionNote || undefined })
      } else if (selectedAction === 'AVAILABLE') {
        await updateAsset(asset.id, { status: 'AVAILABLE' })
      } else if (selectedAction === 'INSTALLED') {
        await markAssetInstalled(asset.id, { location: installLocation.trim(), note: actionNote || undefined })
      } else if (selectedAction === 'UNINSTALL') {
        await uninstallAsset(asset.id, { note: actionNote || undefined })
      }
      notifications.show({ color: 'teal', message: `${t('scan.statusUpdated')} ${selectedAction}` })
      const res = await scanLookupAsset(asset.assetCode)
      if (res.data.found) setAsset(res.data)
      closeAction()
      setSelectedAction(null)
      setActionNote('')
      setInstallLocation('')
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? t('scan.genericError') })
    } finally { setSaving(false) }
  }

  const openRepairModalWithVendors = async () => {
    try {
      if (repairVendors.length === 0) {
        const v = await getVendors()
        setRepairVendors(v.data ?? [])
      }
    } catch {}
    setRepairModalOpen(true)
  }

  const handleActionClick = async (action: string) => {
    if (!asset) return
    if (action === 'REPAIR') {
      openAskReplace()
    } else {
      setSelectedAction(action)
      setActionNote('')
      setInstallLocation('')
      if (action === 'INSTALLED' && locations.length === 0) {
        try { const res = await getLocations(); setLocations(res.data ?? []) } catch {}
      }
      openAction()
    }
  }

  const handleShareAsset = async (a: any) => {
    const shareUrl = `${window.location.origin}/assets?asset=${encodeURIComponent(a.assetCode)}`
    const lines = [
      `📦 ${a.name}`,
      `🏷 ${a.assetCode}`,
      a.serialNumber   ? `🔢 S/N: ${a.serialNumber}` : null,
      a.status         ? `📌 Status: ${a.status}` : null,
      a.category       ? `📁 ${a.category}${a.subCategory ? ` / ${a.subCategory}` : ''}` : null,
      a.departmentCode ? `🏢 ${a.departmentCode}` : null,
      a.location       ? `📍 ${a.location}` : null,
      a.owner          ? `👤 ${a.owner}` : null,
      '',
      `🔗 ${shareUrl}`,
    ].filter(l => l !== null).join('\n')

    const shareData = { title: `AssetOS — ${a.assetCode}`, text: lines, url: shareUrl }
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData)
      } else {
        await navigator.clipboard.writeText(shareUrl)
        notifications.show({ color: 'teal', message: t('scan.copiedToClipboard') })
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        notifications.show({ color: 'red', message: t('scan.shareFailed') })
      }
    }
  }

  const handleLineShare = (a: any) => {
    const shareUrl = `${window.location.origin}/assets?asset=${encodeURIComponent(a.assetCode)}`
    const text = `📦 ${a.name} (${a.assetCode})\n🔗 ${shareUrl}`
    window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`, '_blank', 'width=600,height=600')
  }

  const reset = () => {
    decodedRef.current = false
    setAsset(null)
    setNotFound(false)
    setNotFoundCode('')
    setManualInput('')
    // เปิดกล้องสแกนต่อทันที — ผู้ใช้กด "Scan Again" คาดหวังว่ากล้องจะเปิดเลย
    // ไม่ใช่กลับไปหน้าจอที่ต้องกด "Open Camera" ซ้ำอีกครั้ง
    startScanner()
  }

  return (
    <Box p={isSmall ? 12 : 24} style={{ maxWidth: 540, margin: '0 auto' }}>
      {/* Header */}
      <Group mb="lg" justify="space-between">
        <Group gap={10}>
          <ActionIcon variant="subtle" color="gray" onClick={() => navigate(-1)}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Box>
            <Text fw={700} size="lg" style={{ fontFamily: 'Inter, sans-serif' }}>{t('scan.title')}</Text>
            <Text size="xs" c="dimmed">{t('scan.subtitle')}</Text>
          </Box>
        </Group>
        <Group gap={6}>
          {asset && (
            <ActionIcon
              variant="light" color="violet" size="lg" radius="lg"
              onClick={() => { openMoveHistory(); loadMoveHistory(asset.id) }}
              title={t('scan.movementHistory')}
            >
              <IconHistory size={18} />
            </ActionIcon>
          )}
          <ActionIcon
            variant="light" color="gray" size="lg" radius="lg"
            onClick={openScanLog}
            title={t('scan.scanLog')}
          >
            <IconClock size={18} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Camera view — render เสมอไม่ขึ้นกับ asset/notFound เพื่อกัน videoRef เป็น null
          ตอนเรียก startScanner() ทันทีหลัง reset() (React state update เป็น async) */}
      <Paper withBorder radius="lg" mb={(!asset && !notFound) ? 'md' : 0}
        style={{ overflow: 'hidden', background: '#000', minHeight: scanning ? 300 : 0, position: 'relative' }}>
        <video ref={videoRef} style={{ width: '100%', height: '100%', display: scanning ? 'block' : 'none', objectFit: 'cover' }} autoPlay muted playsInline />
        {scanning && (
          <Box style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box style={{
              width: '70%', height: 120, border: '2px solid rgba(20, 184, 166, 0.9)', borderRadius: 12,
              boxShadow: '0 0 0 9999px rgba(0,0,0,0.35)',
            }} />
          </Box>
        )}
      </Paper>

      {/* Scanner area */}
      {!asset && !notFound && (
        <Stack gap="md">
          {scanning && (
            <Text ta="center" size="xs" c="dimmed">{t('scan.tipDifficult')}</Text>
          )}

          {!scanning ? (
            <Button
              fullWidth size="md" radius="lg"
              leftSection={<IconScan size={18} />}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}
              onClick={startScanner}>
              {t('scan.openCamera')}
            </Button>
          ) : (
            <Button fullWidth size="md" radius="lg" variant="light" color="red"
              leftSection={<IconX size={18} />} onClick={stopScanner}>
              {t('scan.stopScanning')}
            </Button>
          )}

          <Text ta="center" size="sm" c="dimmed">{t('scan.orType')}</Text>

          {/* Manual input */}
          <Group gap="sm">
            <TextInput
              {...{placeholder: t('scan.placeholder')}}
              leftSection={<IconSearch size={16} />}
              value={manualInput}
              onChange={e => setManualInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLookup(manualInput)}
              style={{ flex: 1 }}
              radius="lg"
            />
            <Button radius="lg" onClick={() => handleLookup(manualInput)} loading={loading}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}>
              {t('scan.search')}
            </Button>
          </Group>
        </Stack>
      )}

      {loading && <Center p="xl"><Loader color="teal" /></Center>}

      {/* ── Asset Found ── */}
      {asset && (
        <Stack gap="md">
          <Paper withBorder radius="lg" p="md" style={{ background: '#f0fdf4', borderColor: '#86efac' }}>
            <Group gap="xs" mb={6}>
              <ThemeIcon color="teal" variant="light" radius="xl" size="sm">
                <IconCheck size={14} />
              </ThemeIcon>
              <Text size="xs" fw={600} c="teal">{t("scan.found")}</Text>
            </Group>
            <Text fw={700} size="xl" style={{ lineHeight: 1.3 }}>{asset.name}</Text>
            <Text size="md" c="dimmed" fw={600} mb={8}>{asset.assetCode}</Text>
            <Group gap={6} mb={8} wrap="wrap">
              <Badge color={STATUS_COLOR[asset.status] ?? 'gray'} variant="filled" size="md">
                {STATUS_LABEL[asset.status] ?? asset.status}
              </Badge>
              {asset.category && (
                <Badge color="violet" variant="light" size="md">{asset.category}</Badge>
              )}
              {asset.departmentCode && (
                <Badge color="blue" variant="light" size="md">🏢 {asset.departmentCode}</Badge>
              )}
            </Group>
            {asset.serialNumber && (
              <Text size="sm" c="dimmed">S/N: {asset.serialNumber}</Text>
            )}
            {asset.location && (
              <Text size="sm" c="dimmed">📍 {asset.location}</Text>
            )}
          </Paper>

          {/* Send to Repair — ปุ่มใหญ่เด่น เข้าถึงไวที่สุดสำหรับงานหลักของหน้านี้ */}
          {asset.status !== 'REPAIR' ? (
            <Button
              fullWidth radius="lg" size="lg"
              color="orange" variant="filled"
              leftSection={<IconTool size={20} />}
              style={{ minHeight: 56, fontWeight: 700 }}
              onClick={() => handleActionClick('REPAIR')}>
              {t('scan.sendToRepair')}
            </Button>
          ) : (
            <Button
              fullWidth radius="lg" size="lg"
              color="orange" variant="filled"
              leftSection={<IconTool size={18} />}
              onClick={() => navigate(`/maintenance?assetCode=${asset.assetCode}`)}>
              🔧 {t('scan.viewRepairDetail')}
            </Button>
          )}

          {/* Quick Action buttons — เปลี่ยนสถานะอื่นๆ */}
          <Box>
            <Text size="xs" fw={600} c="dimmed" mb={8} style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t('scan.changeStatus')}
            </Text>
            <SimpleGrid cols={2} spacing="sm">
              {QUICK_ACTIONS
                .filter(a => a.value !== 'REPAIR')
                .filter(a => a.value !== 'INSTALLED' || asset.status === 'AVAILABLE')
                .filter(a => a.value !== 'UNINSTALL' || asset.status === 'INSTALLED')
                .map(action => (
                  <Button
                    key={action.value}
                    variant={asset.status === action.value ? 'filled' : 'light'}
                    color={action.color}
                    radius="lg"
                    size="md"
                    style={{ fontWeight: asset.status === action.value ? 700 : 500, minHeight: 52 }}
                    onClick={() => handleActionClick(action.value)}>
                    {t(action.labelKey)}
                  </Button>
              ))}
            </SimpleGrid>
          </Box>

          {/* View Detail + extra actions for AVAILABLE */}
          <Stack gap="sm">
            <Button
              fullWidth radius="lg" size="md"
              variant="filled"
              style={{ background: '#6366f1', color: '#fff', fontWeight: 600 }}
              leftSection={<IconDeviceDesktop size={16} />}
              onClick={() => navigate(`/assets?highlight=${asset.assetCode}`)}>
              {t('scan.viewFullDetail')}
            </Button>
            {/* เดิมโชว์เฉพาะ asset.status === 'AVAILABLE' เหมือนที่เคยเป็นบั๊กใน AssetQuickView.tsx —
            แก้จุดนั้นไปแล้วแต่ลืมจุดนี้ ปุ่มนี้เป็นปุ่มแยกต่างหาก ไม่ได้ผ่าน AssetQuickView.tsx เลย
            backend swapAssets() ไม่จำกัดสถานะ ตัด condition ทิ้งให้ตรงกับพฤติกรรมจริง */}
            <Button
              fullWidth radius="lg" size="md"
              variant="light" color="teal"
              leftSection={<IconArrowLeft size={16} style={{ transform: 'rotate(90deg) scaleX(-1)' }} />}
              onClick={() => setGeneralSwapModalOpen(true)}>
              ⇄ {t('scan.swapLocationOwner')}
            </Button>
            <Button
              fullWidth radius="lg" size="md"
              variant="light" color="cyan"
              leftSection={<IconShare size={16} />}
              onClick={() => handleShareAsset(asset)}>
              {t('scan.shareAsset')}
            </Button>
          </Stack>

          <Button fullWidth variant="subtle" color="gray" radius="lg" size="sm" onClick={reset}>
            🔄 {t('scan.scanAgain')}
          </Button>
        </Stack>
      )}

      {/* ── Asset Not Found ── */}
      {notFound && (
        <Stack gap="md">
          <Alert
            icon={<IconAlertCircle size={18} />}
            color="orange" variant="light" radius="lg">
            <Text fw={600}>{t('scan.notFound')}</Text>
            <Text size="sm" mt={4}>
              {t('scan.notFoundDetail')}: <b>{notFoundCode}</b>
            </Text>
          </Alert>

          <Button
            fullWidth size="md" radius="lg"
            leftSection={<IconPlus size={16} />}
            style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}
            onClick={() => navigate(`/assets?action=add&serial=${encodeURIComponent(notFoundCode)}`)}>
            {t('scan.addNewAsset')}
          </Button>

          <Button fullWidth variant="outline" radius="lg" color="gray" onClick={reset}>
            {t('scan.scanAgain')}
          </Button>
        </Stack>
      )}

      {/* ── Quick Action Confirm Modal ── */}
      <Modal opened={actionOpened} onClose={closeAction} radius="lg" size="sm"
        title={
          <Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>
            {t('scan.confirmStatusChange')}
          </Text>
        }>
        <Stack gap="md">
          {asset && (
            <Paper withBorder p="sm" radius="md" style={{ background: '#f8fafc' }}>
              <Text size="sm" fw={700}>{asset.name}</Text>
              <Text size="xs" c="dimmed">{asset.assetCode}</Text>
              <Group gap={6} mt={4}>
                <Badge color={STATUS_COLOR[asset.status] ?? 'gray'} variant="light" size="sm">
                  {STATUS_LABEL[asset.status] ?? asset.status}
                </Badge>
                <Text size="xs" c="dimmed">→</Text>
                <Badge
                  color={QUICK_ACTIONS.find(a => a.value === selectedAction)?.color ?? 'gray'}
                  variant="filled" size="sm">
                  {(() => { const a = QUICK_ACTIONS.find(a => a.value === selectedAction); return a ? t(a.labelKey) : selectedAction })()}
                </Badge>
              </Group>
            </Paper>
          )}
          {selectedAction === 'INSTALLED' && (
            <Select
              label={t('scan.installLocation', 'Installed Location') + ' *'}
              placeholder={t('scan.selectLocation', 'Select location')}
              data={locations.map((l) => ({ value: l.name, label: `${localizedName(l, i18n.language)} (${l.code})` }))}
              value={installLocation || null}
              onChange={v => setInstallLocation(v ?? '')}
              searchable
              radius="md"
            />
          )}
          <Textarea
            label={t('scan.noteOptional')}
            placeholder={t('scan.notePlaceholder')}
            minRows={2}
            value={actionNote}
            onChange={e => setActionNote(e.target.value)}
            radius="md"
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeAction}>{t('common.cancel')}</Button>
            <Button
              loading={saving}
              onClick={handleQuickAction}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}>
              {t('common.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Repair Job Modal (simple mode) ── */}
      <RepairJobModal
        opened={repairModalOpen}
        onClose={() => { setRepairModalOpen(false); setPendingReplaceSwap(false) }}
        onSuccess={async (job) => {
          const res = await scanLookupAsset(asset?.assetCode ?? '')
          if (res.data.found) setAsset(res.data)
          // ── ถ้ามาจาก flow "ซ่อม + มีเครื่องแทน" ให้ต่อด้วย swap-with-spare ทันที ──
          // ต้องสร้างงานซ่อมให้เสร็จก่อนเสมอ เพราะ swapWithSpare() ต้องมี job.id ที่มีอยู่จริง
          if (pendingReplaceSwap && job?.id) {
            setSwapJobId(job.id)
            setSwapModalOpen(true)
          }
          setPendingReplaceSwap(false)
        }}
        repairAssets={asset ? [asset] : []}
        preselectedAssetId={asset?.id ?? null}
        vendors={repairVendors}
        consumableItems={[]}
        allAssets={[]}
        mode="simple"
      />

      {/* ── Swap with Spare Asset — ใช้ตัวเดียวกับเว็บ (หน้า Maintenance) เรียก
          swapWithSpare() ที่ถูกต้อง ไม่ใช่ swapAssets() ทั่วไป ── */}
      <SwapWithSpareModal
        opened={swapModalOpen}
        onClose={() => setSwapModalOpen(false)}
        jobId={swapJobId}
        originalAssetId={asset?.id ?? null}
        originalAssetCode={asset?.assetCode}
        originalSerialNumber={asset?.serialNumber}
        originalCategory={asset?.category}
        originalLocation={asset?.location}
        onSuccess={async () => {
          const res = await scanLookupAsset(asset?.assetCode ?? '')
          if (res.data.found) setAsset(res.data)
        }}
      />

      {/* ── Swap Location & Owner ทั่วไป (ไม่เกี่ยวกับซ่อม) — ปุ่มแยกต่างหาก ── */}
      <SwapAssetModal
        opened={generalSwapModalOpen}
        onClose={() => setGeneralSwapModalOpen(false)}
        onSuccess={async () => {
          const res = await scanLookupAsset(asset?.assetCode ?? '')
          if (res.data.found) setAsset(res.data)
        }}
        assetA={asset}
      />

      {/* ── ถามก่อนแจ้งซ่อม: มีเครื่องมาแทนไหม ── */}
      <Modal opened={askReplaceOpen} onClose={closeAskReplace} radius="lg" size="sm"
        title={<Text fw={600}>{t('scan.askReplaceTitle', 'Replace while repairing?')}</Text>}>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t('scan.askReplaceBody', 'Is there a substitute device to swap in while this one is being repaired?')}
          </Text>
          <Group grow>
            <Button variant="default" radius="lg" onClick={async () => {
              closeAskReplace()
              await openRepairModalWithVendors()
            }}>
              {t('scan.noReplace', 'No, just repair')}
            </Button>
            <Button radius="lg"
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}
              onClick={async () => {
                closeAskReplace()
                setPendingReplaceSwap(true)
                await openRepairModalWithVendors()
              }}>
              {t('scan.yesReplace', 'Yes, scan replacement')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Scan Log Drawer: ประวัติ asset ที่สแกนล่าสุดใน session นี้ ── */}
      <Drawer opened={scanLogOpened} onClose={closeScanLog} position="bottom" size="70%" radius="lg"
        title={<Text fw={700}>{t('scan.scanLog')}</Text>}>
        {scanLog.length === 0 ? (
          <Center p="xl"><Text size="sm" c="dimmed">{t('scan.noScanLog')}</Text></Center>
        ) : (
          <ScrollArea h="100%">
            <Stack gap={6}>
              {scanLog.map((s, i) => (
                <Paper key={`${s.id}-${i}`} withBorder radius="md" p="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => { closeScanLog(); handleLookup(s.assetCode) }}>
                  <Group justify="space-between" wrap="nowrap">
                    <Box style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} truncate>{s.name}</Text>
                      <Text size="xs" c="dimmed">{s.assetCode}</Text>
                    </Box>
                    <Stack gap={2} align="flex-end">
                      <Badge size="xs" color={STATUS_COLOR[s.status] ?? 'gray'} variant="light">{s.status}</Badge>
                      <Text size="xs" c="dimmed">
                        {new Date(s.scannedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                      </Text>
                    </Stack>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Drawer>

      {/* ── Movement History Drawer: ประวัติการเคลื่อนไหวของ asset ที่กำลังแสดงอยู่ ── */}
      <Drawer opened={moveHistoryOpened} onClose={closeMoveHistory} position="bottom" size="70%" radius="lg"
        title={<Text fw={700}>{t('scan.movementHistory')} — {asset?.assetCode}</Text>}>
        {loadingMoveHistory ? (
          <Center p="xl"><Loader size="sm" color="teal" /></Center>
        ) : moveHistory.length === 0 ? (
          <Center p="xl"><Text size="sm" c="dimmed">{t('scan.noMovementHistory')}</Text></Center>
        ) : (
          <ScrollArea h="100%">
            <Stack gap={8}>
              {moveHistory.map((h: any, i: number) => (
                <Box key={i} p="xs" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <Group justify="space-between" mb={2}>
                    <Badge size="xs" color="gray" variant="light" tt="capitalize">
                      {h.type?.replace(/_/g, ' ')}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {new Date(h.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Text>
                  </Group>
                  {(h.fromValue || h.toValue) && (
                    <Text size="xs">{h.fromValue || '—'} → {h.toValue || '—'}</Text>
                  )}
                  {h.performedBy && <Text size="xs" c="dimmed">{t('scan.by')}: {h.performedBy}</Text>}
                  {h.note && <Text size="xs" c="dimmed" fs="italic">{h.note}</Text>}
                </Box>
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Drawer>
    </Box>
  )
}
